import { useMemo, useState } from "react";
import {
  List,
  TextField,
  WrapperField,
  DatagridConfigurable,
  WithRecord,
  DateField,
  BooleanField,
  NumberField,
  BulkDeleteWithConfirmButton,
  useResourceContext,
  useTheme,
  FunctionField,
} from "react-admin";
import FactoryDebtCorrectionAddDialog from "../../components/factoryDebtCorrections/create/FactoryDebtCorrectionAddDialog";
import FactoryDebtCorrectionEditDialog from "../../components/factoryDebtCorrections/edit/FactoryDebtCorrectionEditDialog";
import factoryDebtCorrectionFilterList from "../../components/factoryDebtCorrections/filter/factoryDebtCorrectionFilterList";
import TableListActions from "../../components/tableListActions/TableListActions";
import FactoryDebtCorrectionExpandList from "../../components/factoryDebtCorrections/expand/FactoryDebtCorrectionExpandList";
import EditBtn from "../../components/editBtn/EditBtn";
import ConfirmBtn from "../../components/confirmBtn/ConfirmBtn";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import PrintBtn from "../../components/printBtn/PrintBtn";
import PrintHandler from "../../components/printHandler/PrintHandler";
import PrintContent from "../../components/factoryDebtCorrections/print/PrintContent";
import getFirstAndLastDateOfCurrentMonth from "../../utils/firstLastDate";
import FactoryDebtCorrectionFooterList from "../../components/factoryDebtCorrections/footer/FactoryDebtCorrectionFooterList";
import MyDatagridBody from "../../components/common/MyDatagridBody";
import useCompanyScopedListFilters from "../../hooks/useCompanyScopedListFilters";

const [firstDate, lastDate] = getFirstAndLastDateOfCurrentMonth();

const FactoryDebtCorrectionList = () => {
  const resource = useResourceContext();
  const { permissions, canSeeCompanyColumn, filterDefaultValues } =
    useCompanyScopedListFilters({
      start_date: firstDate,
      end_date: lastDate,
    });
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [recordId, setRecordId] = useState();
  const [resetPrintContent, setResetPrintContent] = useState();
  const [printRecord, setPrintRecord] = useState({}); // initial value wajib object kosong untuk cek key id di dependency useeffect DeliveryNote.js
  const [theme] = useTheme("light");

  const handleClickOpenAdd = () => {
    setOpenAdd(true);
  };
  const handleClickOpenEdit = (recId) => () => {
    setRecordId(recId);
    setOpenEdit(true);
  };
  const list = useMemo(
    () => (
      <List
        title="mine.list_title.factory_debt_correction"
        empty={false}
        actions={
          <TableListActions
            createHandler={handleClickOpenAdd}
            exporter={false}
            createPermission="create_factory_debt_correction"
          />
        }
        filters={factoryDebtCorrectionFilterList(permissions)}
        queryOptions={{ meta: { sort: ["created_at DESC"] } }} // overide default sort
        filterDefaultValues={filterDefaultValues}
      >
        <DatagridConfigurable
          body={<MyDatagridBody footer={<FactoryDebtCorrectionFooterList />} />}
          bulkActionButtons={<BulkDeleteWithConfirmButton />}
          expand={<FactoryDebtCorrectionExpandList />}
          sx={{
            "& .RaDatagrid-expandedPanel > td": {
              p: 0,
              bgcolor: theme !== "dark" ? "#E7EBF0" : "grey.800",
            },
          }}
        >
          {canSeeCompanyColumn ? (
            <TextField label="mine.label.depo" source="company.alias" />
          ) : null}
          <TextField source="code" label="mine.label.receipt_no" />
          <DateField
            source="date"
            label="mine.label.date"
            options={{
              year: "numeric",
              month: "short",
              day: "2-digit",
            }}
          />
          <TextField label="Supplier" source="supplier.name" />
          <FunctionField
            label="Lawan GL"
            render={(record) => {
              for (
                let i = 0;
                i < record.factory_debt_correction_journals.length;
                i++
              ) {
                const elm = record.factory_debt_correction_journals[i];
                if (elm.chart_of_account.code !== "2102") {
                  return (
                    elm.chart_of_account.code +
                    " - " +
                    elm.chart_of_account.name
                  );
                }
              }
              return "";
            }}
          />
          <NumberField source="total" />
          <TextField source="note" label="mine.label.note" />
          <BooleanField
            source="approved"
            label="mine.label.approved"
            textAlign="center"
          />
          <WrapperField label="mine.label.actions" textAlign="center">
            <WithRecord
              render={(record) => {
                return !record.approved ? (
                  <>
                    <EditBtn
                      editHandler={handleClickOpenEdit(record.id)}
                      sx={{ mr: 1 }}
                    />
                    <ConfirmBtn
                      title="mine.message.approve"
                      content="mine.message.approve_content"
                      label="mine.btn.approve"
                      resource={resource + "/approve"}
                      startIcon={<DoneIcon />}
                    />
                  </>
                ) : (
                  <>
                    {permissions && permissions.administrator && (
                      <ConfirmBtn
                        title="mine.message.unapprove"
                        content="mine.message.unapprove_content"
                        label="mine.label.unapprove"
                        resource={resource + "/unapprove"}
                        startIcon={<CloseIcon />}
                        color="error"
                      />
                    )}
                    <PrintBtn
                      dialogProps={{ title: "mine.dialog.print" }}
                      recordId={record.id}
                      setPrintRecord={setPrintRecord}
                      setResetPrintContent={setResetPrintContent}
                    />
                  </>
                );
              }}
            />
          </WrapperField>
        </DatagridConfigurable>
      </List>
    ),
    [theme, permissions, canSeeCompanyColumn, filterDefaultValues]
  );
  return (
    <>
      {list}
      <FactoryDebtCorrectionAddDialog open={openAdd} setOpen={setOpenAdd} />
      <FactoryDebtCorrectionEditDialog
        open={openEdit}
        setOpen={setOpenEdit}
        recordId={recordId}
      />
      <PrintHandler
        record={printRecord}
        resetPrintContent={resetPrintContent}
        Content={PrintContent}
      />
    </>
  );
};

export default FactoryDebtCorrectionList;
